import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Loader2, RefreshCw, AlertTriangle, CheckCircle2 } from 'lucide-react';

/**
 * QC-side Change Package modal.
 * Used from 3 places (May 20, 2026):
 *   1. QC Review & Submit (empty-checks state) — fix-up for cases created
 *      before a package was associated to the entity.
 *   2. QC Case Review (Edit Mode) — swap package mid-flow.
 *   3. Verification Cases (All Cases) — admin action button.
 *
 * Backend: POST /api/cases/{case_id}/change-package
 * - Refuses if any check is past pending/assigned/in_progress.
 * - Preserves existing checks whose type still exists in the new package;
 *   drops checks whose type isn't in the new package; tops up new types.
 *
 * May 21, 2026 — Replaced `window.confirm` with an inline 2-step
 * confirmation. The Emergent preview runs in a sandboxed iframe where
 * `window.confirm()` silently returns false, so the prior version made
 * the Apply Change button look completely inert.
 */
export default function ChangePackageModal({ open, onOpenChange, caseId, currentPackageId, onChanged }) {
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState('');
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Two-step inline confirmation — flips to true on first Apply click, on
  // second click we actually POST. Reset when modal closes or selection changes.
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState(null); // success preview after POST

  useEffect(() => {
    if (!open) {
      setConfirming(false);
      setResult(null);
      setError(null);
      return;
    }
    setError(null);
    setResult(null);
    setSelectedPackage(currentPackageId || '');
    setLoadingPackages(true);
    (async () => {
      try {
        const res = await axios.get('/packages');
        setPackages(res.data || []);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load packages');
      } finally {
        setLoadingPackages(false);
      }
    })();
  }, [open, currentPackageId]);

  // Reset confirmation step when the selection changes — prevents
  // accidentally applying after picking a different package mid-confirm.
  useEffect(() => {
    setConfirming(false);
    setError(null);
    setResult(null);
  }, [selectedPackage]);

  const isSamePackage = useMemo(
    () => Boolean(selectedPackage) && selectedPackage === currentPackageId,
    [selectedPackage, currentPackageId]
  );

  const doApply = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await axios.post(`/cases/${caseId}/change-package`, { package_id: selectedPackage });
      setResult(res.data || {});
      toast.success(res.data?.message || 'Package changed');
      // Keep modal open briefly so the user sees the success summary,
      // THEN notify the parent (which usually re-fetches and closes us).
      setTimeout(() => {
        if (onChanged) onChanged(res.data);
        onOpenChange(false);
      }, 1800);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.code === 'checks_already_progressed') {
        setError(detail);
      } else {
        const msg = typeof detail === 'string' ? detail : (detail?.message || 'Change package failed');
        toast.error(msg);
        setError({ code: 'generic', message: msg });
      }
      setConfirming(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleApply = () => {
    if (!selectedPackage) {
      toast.error('Pick a package');
      return;
    }
    if (!confirming) {
      // First click — switch to confirmation state. No native dialog.
      setConfirming(true);
      return;
    }
    doApply();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="change-package-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-indigo-600" />
            Change Package for {caseId}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs flex gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
            <div>
              <strong>Heads-up:</strong> Existing PENDING / ASSIGNED / IN-PROGRESS checks whose type remains in the new package will be <strong>preserved</strong>. Checks for types removed by the new package will be <strong>dropped</strong>. Checks past those statuses (insufficient / clear / unable-to-verify / qc-approved / completed) will block the change — Reset Case from Admin first.
            </div>
          </div>

          <div>
            <Label htmlFor="cp-package">New Package</Label>
            <select
              id="cp-package"
              data-testid="change-package-select"
              className="w-full mt-1 border rounded-md p-2 text-sm"
              value={selectedPackage}
              onChange={(e) => setSelectedPackage(e.target.value)}
              disabled={loadingPackages || submitting || !!result}
            >
              <option value="">— Select a package —</option>
              {packages.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({(p.checks || []).length} checks){currentPackageId === p.id ? ' • current' : ''}
                </option>
              ))}
            </select>
          </div>

          {isSamePackage && !result && (
            <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600" data-testid="change-package-same-warning">
              You've selected the case's <strong>current</strong> package. Applying will keep all existing checks unchanged (no-op).
            </div>
          )}

          {confirming && !result && (
            <div className="rounded-md bg-indigo-50 border border-indigo-300 p-3 text-xs text-indigo-800" data-testid="change-package-confirm-step">
              <div className="font-semibold mb-1">Confirm package change?</div>
              <div>
                Click <strong>Apply Change</strong> again to proceed. This will reshuffle the case's checks and record an audit entry. To abort, click <strong>Cancel</strong>.
              </div>
            </div>
          )}

          {error && error.code === 'checks_already_progressed' && (
            <div className="rounded-md bg-rose-50 border border-rose-200 p-3 text-xs" data-testid="change-package-blocked">
              <div className="font-semibold text-rose-700 mb-1">Blocked — {error.blocked_checks.length} check(s) already progressed:</div>
              <ul className="list-disc ml-5">
                {error.blocked_checks.map((c, i) => (
                  <li key={i}><code>{c.check_id}</code> · {c.check_type} · <strong>{c.status}</strong></li>
                ))}
              </ul>
              <div className="mt-2 text-rose-600">Use <strong>Admin → Reset Case</strong> first, then retry.</div>
            </div>
          )}

          {error && error.code === 'generic' && (
            <div className="rounded-md bg-rose-50 border border-rose-200 p-3 text-xs text-rose-700" data-testid="change-package-error">
              {error.message}
            </div>
          )}

          {result && (
            <div className="rounded-md bg-emerald-50 border border-emerald-300 p-3 text-xs text-emerald-800 flex gap-2" data-testid="change-package-success">
              <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" />
              <div>
                <div className="font-semibold">Package changed successfully.</div>
                <div>
                  Checks: {result.previous_checks_count} → {result.new_checks_count}
                  {' '}({result.preserved_count} preserved, {result.added_count} added)
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="change-package-cancel-btn">
            {result ? 'Close' : 'Cancel'}
          </Button>
          {!result && (
            <Button
              data-testid="change-package-apply-btn"
              onClick={handleApply}
              disabled={submitting || !selectedPackage}
              className={confirming ? 'bg-amber-600 hover:bg-amber-700' : 'bg-indigo-600 hover:bg-indigo-700'}
            >
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              {confirming ? 'Confirm — Apply Change' : 'Apply Change'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
